import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../config/api";
import "./ProductDetail.css";
import DropdownSection from "../components/DropdownSection/DropdownSection";
import useCart from "../hooks/useCart";
import useWishlist from "../hooks/useWishlist";
import { FaHeart, FaRegHeart, FaTimes, FaSearch } from "react-icons/fa";
import { FaStar } from "react-icons/fa";
import { FaSync } from "react-icons/fa";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const videoRefs = useRef(new Map());

  // Helper function to determine if URL is a video
  const isVideo = (url) => {
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Safe video play function with error handling
  const safePlayVideo = async (videoElement) => {
    if (!videoElement) return;
    
    try {
      await videoElement.play();
    } catch (error) {
      // Silently handle play errors (like when video is removed from DOM)
      console.log('Video play prevented:', error.message);
    }
  };

  // Safe video pause function
  const safePauseVideo = (videoElement) => {
    if (!videoElement) return;
    
    try {
      videoElement.pause();
    } catch (error) {
      console.log('Video pause prevented:', error.message);
    }
  };

  // Cleanup all videos
  const cleanupVideos = () => {
    videoRefs.current.forEach((videoElement) => {
      if (videoElement) {
        safePauseVideo(videoElement);
      }
    });
    videoRefs.current.clear();
  };
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [pincode, setPincode] = useState("");
  const [deliveryEstimate, setDeliveryEstimate] = useState("");
  const [similarProducts, setSimilarProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Inline zoom state
  const [isZoomEnabled, setIsZoomEnabled] = useState(false);
  const [isInlineZoomVisible, setIsInlineZoomVisible] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const mainImageRef = useRef(null);
  const INLINE_ZOOM_SCALE = 2.35;
  const canUseInlineZoom =
    typeof window === "undefined" || !window.matchMedia
      ? true
      : window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const resetInlineZoom = useCallback(() => {
    setIsInlineZoomVisible(false);
    setZoomPosition({ x: 50, y: 50 });
  }, []);

  const updateInlineZoomPosition = useCallback((clientX, clientY) => {
    if (!mainImageRef.current) return;

    const rect = mainImageRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

    setZoomPosition({
      x: (x / rect.width) * 100,
      y: (y / rect.height) * 100,
    });
  }, []);

  const handleImageMouseEnter = useCallback((e) => {
    if (!canUseInlineZoom || !isZoomEnabled || isVideo(selectedImage)) return;

    setIsInlineZoomVisible(true);
    updateInlineZoomPosition(e.clientX, e.clientY);
  }, [canUseInlineZoom, isZoomEnabled, selectedImage, updateInlineZoomPosition]);

  const handleImageMouseMove = useCallback((e) => {
    if (!canUseInlineZoom || !isZoomEnabled || isVideo(selectedImage)) return;

    setIsInlineZoomVisible(true);
    updateInlineZoomPosition(e.clientX, e.clientY);
  }, [canUseInlineZoom, isZoomEnabled, selectedImage, updateInlineZoomPosition]);

  const handleImageMouseLeave = useCallback(() => {
    resetInlineZoom();
  }, [resetInlineZoom]);

  const handleMainImageClick = useCallback((e) => {
    if (!canUseInlineZoom || isVideo(selectedImage)) return;

    if (isZoomEnabled) {
      setIsZoomEnabled(false);
      resetInlineZoom();
      return;
    }

    setIsZoomEnabled(true);
    setIsInlineZoomVisible(true);
    updateInlineZoomPosition(e.clientX, e.clientY);
  }, [canUseInlineZoom, isZoomEnabled, selectedImage, resetInlineZoom, updateInlineZoomPosition]);

  // Fetch product and reviews together
  const fetchProductAndReviews = async () => {
    try {
      const res = await axios.get(`${API_BASE}/products/${id}`);
      setProduct(res.data);
      setSelectedImage(res.data.images[0]);
      if (res.data._id) {
        setInWishlist(isInWishlist(res.data._id));
      }
      // Fetch reviews for this product
      const reviewsRes = await axios.get(`${API_BASE}/reviews/product/${id}`);
      setReviews(reviewsRes.data.reviews || []);
    } catch (err) {
      console.error("Error fetching product or reviews:", err);
      setReviews([]);
    }
  };

  useEffect(() => {
    // If navigated with refreshReviews flag, refresh product and reviews
    if (location.state && location.state.refreshReviews) {
      fetchProductAndReviews();
      // Optionally, clear the flag so it doesn't keep refreshing
      window.history.replaceState({}, document.title);
    } else {
      fetchProductAndReviews();
    }
    // eslint-disable-next-line
  }, [id, isInWishlist]);

  useEffect(() => {
    const fetchSimilarProducts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/products`, {
          params: {
            material: product?.material,
            product: product?.product,
            limit: 10,
          },
        });
        const filtered = res.data.filter((p) => p._id !== product._id);
        setSimilarProducts(filtered);
      } catch (err) {
        console.error("Error fetching similar products:", err);
      }
    };

    if (product) {
      fetchSimilarProducts();
    }
  }, [product]);

  useEffect(() => {
    // Listen for custom refresh event
    const handler = () => fetchProductAndReviews();
    window.addEventListener('refreshProductReviews', handler);
    return () => window.removeEventListener('refreshProductReviews', handler);
  }, []);

  // Cleanup videos on unmount
  useEffect(() => {
    return () => {
      cleanupVideos();
    };
  }, []);

  useEffect(() => {
    setIsZoomEnabled(false);
    resetInlineZoom();
  }, [selectedImage, resetInlineZoom]);

  const handleAddToCart = () => {
    const success = addToCart(product, quantity);
    if (success) {
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  const handleBuyNow = () => {
    const success = addToCart(product, quantity);
    if (success) {
      navigate("/cart");
    }
  };
  
  const handleWishlistToggle = () => {
    if (inWishlist) {
      removeFromWishlist(product._id);
      setInWishlist(false);
    } else {
      const success = addToWishlist(product);
      if (success) {
        setInWishlist(true);
      }
    }
  };
  
  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };
  
  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const checkPincode = () => {
    if (/^\d{6}$/.test(pincode)) {
      const deliveryDate = new Date();
      deliveryDate.setDate(deliveryDate.getDate() + 5);
      setDeliveryEstimate(`Delivery by ${deliveryDate.toDateString()}`);
    } else {
      setDeliveryEstimate("Please enter a valid 6-digit pincode.");
    }
  };

  const openImageModal = () => {
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeImageModal();
      }
    };

    if (isImageModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isImageModalOpen]);

  if (!product) return <div>Loading...</div>;

  const mainImageZoomStyle =
    canUseInlineZoom && !isVideo(selectedImage) && isZoomEnabled && isInlineZoomVisible
      ? {
          transform: `scale(${INLINE_ZOOM_SCALE})`,
          transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
        }
      : undefined;

  return (
    <div className="product-detail-wrapper">
      <div className="product-detail-container">
        {/* Left: Media (Images/Videos) */}
        <div className="image-section">
          <div className="thumbnail-images">
            {product.images.map((media, i) => (
              <div key={i} className="thumbnail-wrapper">
                {isVideo(media) ? (
                  <video
                    src={media}
                    className={`thumbnail-video ${selectedImage === media ? "active" : ""}`}
                    onClick={() => setSelectedImage(media)}
                    muted
                    onError={(e) => {
                      console.log('Thumbnail video error:', e.target.error);
                    }}
                    ref={(el) => {
                      if (el) {
                        videoRefs.current.set(`thumb-${i}`, el);
                      } else {
                        videoRefs.current.delete(`thumb-${i}`);
                      }
                    }}
                  />
                ) : (
                  <img
                    src={media}
                    alt={`thumb-${i}`}
                    className={selectedImage === media ? "active" : ""}
                    onClick={() => setSelectedImage(media)}
                  />
                )}
                {isVideo(media) && (
                  <div className="video-indicator">🎥</div>
                )}
              </div>
            ))}
          </div>
          <div
            className={`main-image ${!isVideo(selectedImage) && canUseInlineZoom ? "zoomable-image" : ""} ${isZoomEnabled ? "zoom-enabled" : ""}`}
            ref={mainImageRef}
            onClick={!isVideo(selectedImage) ? handleMainImageClick : undefined}
            onMouseEnter={handleImageMouseEnter}
            onMouseLeave={handleImageMouseLeave}
            onMouseMove={handleImageMouseMove}
          >
            {isVideo(selectedImage) ? (
              <video
                src={selectedImage}
                controls
                className="main-video"
                onClick={openImageModal}
                onError={(e) => { console.log('Main video error:', e.target.error); }}
                ref={(el) => {
                  if (el) videoRefs.current.set('main', el);
                  else videoRefs.current.delete('main');
                }}
              />
            ) : (
              <img
                src={selectedImage}
                alt="Main"
                className="main-product-image"
                draggable={false}
                style={mainImageZoomStyle}
              />
            )}
            {!isVideo(selectedImage) && canUseInlineZoom && (
              <div className={`zoom-hint ${isZoomEnabled ? "active" : ""}`}>
                <FaSearch />
                <span>{isZoomEnabled ? "Move inside image. Click again to reset." : "Click image to zoom"}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right: Details */}
        <div className="detail-section">
          <h1>{product.title}</h1>
          {/* Show average star rating under product name */}
          <div style={{display:'flex',alignItems:'center',margin:'8px 0'}}>
            {[1,2,3,4,5].map(star => (
              <FaStar key={star} color={star <= (product.rating || 0) ? '#FFD700' : '#ccc'} size={22} />
            ))}
            <span style={{marginLeft:8,fontWeight:500,fontSize:16}}>
              {product.rating ? product.rating.toFixed(1) : '0.0'} / 5
            </span>
          </div>
          {/* Remove old demo rating text */}

          <div className="price-box">
            <span className="offer-price">₹{product.offerPrice}</span>
            {product.price && (
              <span className="original-price">₹{product.price}</span>
            )}
          </div>

          <div className="quantity-selector">
            <label>Quantity</label>
            <div className="quantity-controls">
              <button onClick={decrementQuantity} disabled={quantity <= 1}>-</button>
              <span>{quantity}</span>
              <button onClick={incrementQuantity}>+</button>
            </div>
          </div>

          <div className="button-group">
            <button className="buy-now" onClick={handleAddToCart}>
              {addedToCart ? "Added to Cart!" : "Add to Cart"}
            </button>
            <button className="add-to-cart" onClick={handleBuyNow}>Buy Now</button>
            <button className="wishlist-btn" onClick={handleWishlistToggle}>
              {inWishlist ? (
                <>
                  <FaHeart className="heart-filled" /> Remove from Wishlist
                </>
              ) : (
                <>
                  <FaRegHeart /> Add to Wishlist
                </>
              )}
            </button>
          </div>

          <DropdownSection title="Product Description">
            <p>{product.description || "No description available."}</p>
          </DropdownSection>

          <DropdownSection title="Shipping Details">
            <ul>
              {product.shippingDetails?.freeShippingThreshold ? (
                <li>🚚 Free shipping on orders above ₹{product.shippingDetails.freeShippingThreshold}</li>
              ) : (
                <li>🚚 Free shipping on orders above ₹500</li>
              )}
              {product.shippingDetails?.deliveryDays ? (
                <li>📦 Delivery within {product.shippingDetails.deliveryDays}</li>
              ) : (
                <li>📦 Delivery within 4–7 business days</li>
              )}
              {product.shippingDetails?.codAvailable !== false ? (
                <li>💵 Cash on Delivery available</li>
              ) : (
                <li>💵 Cash on Delivery not available</li>
              )}
            </ul>
          </DropdownSection>

          <div className="pincode-check">
            <label>Estimated Delivery</label>
            <div className="pincode-input">
              <input
                type="text"
                placeholder="Enter 6 digit pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
              />
              <button onClick={checkPincode}>Check</button>
            </div>
            {deliveryEstimate && <p className="delivery-estimate">{deliveryEstimate}</p>}
          </div>

        </div>
      </div>

      {/* Media Zoom Modal */}
      {isImageModalOpen && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>
              <FaTimes />
            </button>
            <div className="modal-image-container">
              {isVideo(selectedImage) ? (
                <video 
                  src={selectedImage} 
                  controls 
                  className="modal-video"
                  onError={(e) => {
                    console.log('Modal video error:', e.target.error);
                  }}
                  ref={(el) => {
                    if (el) {
                      videoRefs.current.set('modal-main', el);
                    } else {
                      videoRefs.current.delete('modal-main');
                    }
                  }}
                />
              ) : (
                <img src={selectedImage} alt="Zoomed Product" />
              )}
            </div>
            <div className="modal-thumbnails">
              {product.images.map((media, i) => (
                <div key={i} className="modal-thumbnail-wrapper">
                  {isVideo(media) ? (
                    <video
                      src={media}
                      className={`modal-thumbnail-video ${selectedImage === media ? "active" : ""}`}
                      onClick={() => setSelectedImage(media)}
                      muted
                      onError={(e) => {
                        console.log('Modal thumbnail video error:', e.target.error);
                      }}
                      ref={(el) => {
                        if (el) {
                          videoRefs.current.set(`modal-thumb-${i}`, el);
                        } else {
                          videoRefs.current.delete(`modal-thumb-${i}`);
                        }
                      }}
                    />
                  ) : (
                    <img
                      src={media}
                      alt={`modal-thumb-${i}`}
                      className={selectedImage === media ? "active" : ""}
                      onClick={() => setSelectedImage(media)}
                    />
                  )}
                  {isVideo(media) && (
                    <div className="modal-video-indicator">🎥</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Similar Products */}
      {similarProducts.length > 0 && (
        <div className="similar-products-section">
          <h2>Similar Products</h2>
          <Swiper
            modules={[Navigation]}
            spaceBetween={15}
            slidesPerView={3}
            navigation
            breakpoints={{
              320: { slidesPerView: 1 },
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {similarProducts.map((prod) => (
              <SwiperSlide key={prod._id}>
                <div
                  className="similar-product-card"
                  onClick={() => {
                    navigate(`/product/${prod._id}`);
                    window.scrollTo(0, 0);
                  }}
                >
                  {isVideo(prod.images?.[0]) ? (
                    <div className="similar-product-video-wrapper">
                      <video
                        src={prod.images[0]}
                        className="similar-product-video"
                        muted
                        onMouseEnter={(e) => {
                          try {
                            e.target.play();
                          } catch (error) {
                            console.log('Video play prevented:', error.message);
                          }
                        }}
                        onMouseLeave={(e) => {
                          try {
                            e.target.pause();
                          } catch (error) {
                            console.log('Video pause prevented:', error.message);
                          }
                        }}
                        onError={(e) => {
                          console.log('Similar product video error:', e.target.error);
                        }}
                      />
                      <div className="similar-product-video-indicator">🎥</div>
                    </div>
                  ) : (
                    <img src={prod.images?.[0]} alt={prod.title} />
                  )}
                  <h4>{prod.title}</h4>
                  <p>₹{prod.offerPrice}</p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
      {reviews && (
        <div className="product-reviews-section" style={{marginTop:40,background:'#fff',borderRadius:8,padding:24,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
            <h2 style={{marginBottom:0}}>Customer Reviews</h2>
            {/* Removed top right rating/review count */}
          </div>
          {reviews.length === 0 ? (
            <div>No reviews yet.</div>
          ) : (
            reviews.map(r => (
              <div key={r._id} style={{borderBottom:'1px solid #eee',marginBottom:16,paddingBottom:12}}>
                <div style={{fontWeight:600}}>{r.user?.name || 'User'} &nbsp;
                  {[1,2,3,4,5].map(star => <FaStar key={star} color={star <= r.rating ? '#FFD700' : '#ccc'} size={18} />)}
                </div>
                <div style={{margin:'8px 0'}}>{r.feedback}</div>
                <div style={{fontSize:12,color:'#888'}}>{new Date(r.createdAt).toLocaleString()}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
